import { useRenderer } from '@fox/atoms';
import { IBlockDefinition, IBlockProps } from '@fox/blocks';
import { useI18n, useSubscribeTopicWithHandler } from 'fox-extensions';
import { useState } from 'react';
import { SURVEY_BUTTON_NEXT_BLOCK_TYPE, SURVEY_BUTTON_PREVIOUS_BLOCK_TYPE, SURVEY_BUTTON_SUBMIT_BLOCK_TYPE } from '../../../form-block-types';
import { FORM_QUESTION_ANSWERED_TOPIC, FormRenderMode, useFormRuntimeContext } from '../../form-runtime-context';
import { FormButtonWrapper } from '../../styled/form-button-wrapper';
import { ButtonBlock } from '../button-block/button-block';
import { ButtonBlockProps } from '../button-block/contracts';
import { checkAllowNext } from '../form-block/util/allow-next';
import { ensureAnswers } from '../form-block/util/ensure-answers';

export const SurveyButtonBlock = ({ id, props, type }: IBlockProps<ButtonBlockProps>) => {
	const {
		submitForm,
		submitButtonDisabled,
		getAnswers,
		formRenderMode,
		goToNextPage,
		goToPreviousPage,
		currentPage: formCurrentPage,
		sections,
		layout
	} = useFormRuntimeContext();
	const render = useRenderer();
	useSubscribeTopicWithHandler(FORM_QUESTION_ANSWERED_TOPIC, () => {
		render();
	});
	const i18n = useI18n();
	const [loading, setLoading] = useState(false);
	let action;

	if (type === SURVEY_BUTTON_NEXT_BLOCK_TYPE) {
		action = goToNextPage;
	} else if (type === SURVEY_BUTTON_PREVIOUS_BLOCK_TYPE) {
		action = goToPreviousPage;
	} else if (type === SURVEY_BUTTON_SUBMIT_BLOCK_TYPE) {
		if (submitForm) {
			action = async () => {
				const answers = getAnswers();
				const toSubmit = await ensureAnswers(answers ?? [], i18n.countryCode);
				await submitForm(toSubmit);
			};
		}
	}
	const currentPage = sections[formCurrentPage ?? 0];
	const allowNext = checkAllowNext({ formRenderMode, currentPage, layout, sections, getAnswers });

	let disabled = false;
	if (
		((type === SURVEY_BUTTON_NEXT_BLOCK_TYPE || type === SURVEY_BUTTON_SUBMIT_BLOCK_TYPE) &&
			!allowNext &&
			formRenderMode !== FormRenderMode.design) ||
		loading
	) {
		disabled = true;
	}

	const disabledButton = disabled || submitButtonDisabled;
	return (
		<div style={{ width: '100%' }}>
			<div title={!disabledButton ? '' : 'Button is disabled because not all required fields are filled'}>
				<FormButtonWrapper
					$disabled={disabledButton}
					onClick={async e => {
						setLoading(true);
						if (type === SURVEY_BUTTON_SUBMIT_BLOCK_TYPE) {
							e.preventDefault();
						}
						if (action && !disabled && !submitButtonDisabled) await action();
						setLoading(false);
					}}
				>
					<ButtonBlock id={id} props={props} type={type} designMode={formRenderMode === FormRenderMode.design} />
				</FormButtonWrapper>
			</div>
		</div>
	);
};

export const SurveyButtonNextBlockRuntime: IBlockDefinition<ButtonBlockProps> = {
	type: SURVEY_BUTTON_NEXT_BLOCK_TYPE,
	Component: SurveyButtonBlock
};

export const SurveyButtonPreviousBlockRuntime: IBlockDefinition<ButtonBlockProps> = {
	type: SURVEY_BUTTON_PREVIOUS_BLOCK_TYPE,
	Component: SurveyButtonBlock
};

export const SurveyButtonSubmitBlockRuntime: IBlockDefinition<ButtonBlockProps> = {
	type: SURVEY_BUTTON_SUBMIT_BLOCK_TYPE,
	Component: SurveyButtonBlock
};
